/**
 * Settings > Industry starter packs (tracker item 76), ADMIN only.
 *
 * A pack seeds items, a price chart, checklist templates, lead sources and
 * recurring presets for one industry in one click. Preview shows exactly what
 * Apply would create, restore or skip (and why); Apply is additive and
 * idempotent (a record that already exists is never changed); "Remove unused
 * starter records" deletes only the pack's records that nothing uses and that
 * were not edited since. Prices are placeholders to review.
 *
 * Only an ADMIN may call /starter-templates: nobody else makes a request.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IStarterTemplatesApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManageStarterTemplates } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import {
  KEEP_REASON_TEXT,
  PRICE_REVIEW_NOTE,
  SKIP_REASON_SHORT,
  countLabel,
  entityLabel,
  groupEntries,
  packCountLines,
  skipReasonText,
} from '../../utils/starterUi';
import { primaryButtonClass, secondaryButtonClass, rowDangerActionClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';

const tap = 'min-h-[44px] sm:min-h-0';

type Panel =
  | { kind: 'plan'; packKey: types.StarterPackKey; intent: 'preview' | 'apply'; preview: types.StarterPreviewResponse }
  | { kind: 'applied'; packKey: types.StarterPackKey; result: types.StarterApplyResponse }
  | { kind: 'removed'; packKey: types.StarterPackKey; result: types.StarterRemoveResponse };

const titleOf = (packs: types.StarterPackSummary[], key: types.StarterPackKey) => packs.find((p) => p.key === key)?.title ?? key;

function ActionTag({ action, done }: { action: 'create' | 'restore' | 'skip'; done?: boolean }) {
  const label = action === 'create' ? (done ? 'Added' : 'Will add') : action === 'restore' ? (done ? 'Restored' : 'Restore') : 'Skipped';
  const cls =
    action === 'skip' ? 'bg-gray-100 text-gray-700' : action === 'restore' ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800';
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

/** Entries grouped by record type, one row each with what happens and, for a skip, why. */
function EntryList({ entries, doneWording }: { entries: types.StarterPlanEntry[]; doneWording?: boolean }) {
  return (
    <div className="space-y-4">
      {groupEntries(entries).map(([type, rows]) => (
        <section key={type} aria-label={`${entityLabel(type)} records`}>
          <h4 className="text-sm font-semibold text-gray-900 capitalize">{countLabel(rows.length, type)}</h4>
          <ul className="mt-1 divide-y divide-gray-100 rounded-md border border-gray-200">
            {rows.map((entry) => (
              <li key={`${entry.entityType}:${entry.name}`} data-action={entry.action} data-reason={entry.reason ?? undefined} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2">
                <span className="min-w-0 flex-1 break-words text-sm text-gray-900">{entry.name}</span>
                <span className="flex min-w-0 flex-wrap items-center gap-2">
                  {entry.action === 'skip' && entry.reason ? (
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                      {doneWording ? 'Skipped: ' : 'Will skip: '}{SKIP_REASON_SHORT[entry.reason] ?? entry.reason}
                    </span>
                  ) : (
                    <ActionTag action={entry.action} done={doneWording} />
                  )}
                </span>
                {entry.action === 'skip' && entry.reason && (
                  <p className="w-full text-xs text-gray-600">{skipReasonText(entry.reason)}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function PackContents({ pack }: { pack: types.StarterPack }) {
  return (
    <div className="mt-3 space-y-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700" aria-label={`${pack.title} contents`}>
      <div>
        <h4 className="font-semibold text-gray-900">Items (placeholder prices)</h4>
        <ul className="mt-1 space-y-0.5">
          {pack.items.map((item) => (
            <li key={item.name} className="break-words">
              {item.name}: ${item.unitPrice} per {item.unitOfMeasure}
              {item.priceChart ? `, priced by ${item.priceChart}` : ''}
            </li>
          ))}
        </ul>
      </div>
      {pack.priceCharts.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900">Price charts</h4>
          <ul className="mt-1 space-y-0.5">
            {pack.priceCharts.map((chart) => (
              <li key={chart.name} className="break-words">{chart.name} ({chart.tiers.length} size bands)</li>
            ))}
          </ul>
        </div>
      )}
      {pack.checklistTemplates.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900">Checklist templates</h4>
          <ul className="mt-1 space-y-0.5">
            {pack.checklistTemplates.map((t) => (
              <li key={t.name} className="break-words">{t.name} ({t.steps.length} steps)</li>
            ))}
          </ul>
        </div>
      )}
      {pack.recurringPresets.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900">Recurring presets</h4>
          <ul className="mt-1 space-y-0.5">
            {pack.recurringPresets.map((p) => (
              <li key={p.name} className="break-words">{p.name}</li>
            ))}
          </ul>
        </div>
      )}
      {pack.leadSources.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900">Lead sources</h4>
          <p className="mt-1 break-words">{pack.leadSources.map((s) => s.name).join(', ')}</p>
        </div>
      )}
    </div>
  );
}

export function StarterPacksPage() {
  const { user } = useAuth();
  const allowed = canManageStarterTemplates(user);
  const api = useMemo<IStarterTemplatesApi>(() => createAPIClient(), []);
  const [packs, setPacks] = useState<types.StarterPackSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The card whose action is running */
  const [busyKey, setBusyKey] = useState<types.StarterPackKey | null>(null);
  const [panel, setPanel] = useState<Panel | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<types.StarterPackKey | null>(null);
  const [contents, setContents] = useState<Partial<Record<types.StarterPackKey, types.StarterPack | 'loading'>>>({});

  const loadPacks = useCallback(async () => {
    setLoadError(null);
    try {
      setPacks(await api.listStarterPacks());
    } catch (err) {
      setLoadError(getErrorMessage(err, 'The starter packs could not be loaded.'));
    }
  }, [api]);

  // Only an ADMIN may read the packs: nobody else makes the request
  useEffect(() => {
    if (allowed) void loadPacks();
  }, [allowed, loadPacks]);

  const run = async (key: types.StarterPackKey, work: () => Promise<void>, failure: string) => {
    setBusyKey(key);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(getErrorMessage(err, failure));
    } finally {
      setBusyKey(null);
    }
  };

  const openPlan = (key: types.StarterPackKey, intent: 'preview' | 'apply') => {
    setConfirmRemove(null);
    return run(key, async () => {
      setPanel({ kind: 'plan', packKey: key, intent, preview: await api.previewStarterPack(key) });
    }, 'The preview could not be loaded.');
  };

  const apply = (key: types.StarterPackKey) =>
    run(key, async () => {
      const result = await api.applyStarterPack(key);
      setPanel({ kind: 'applied', packKey: key, result });
      await loadPacks();
    }, 'The pack could not be applied.');

  const remove = (key: types.StarterPackKey) => {
    setConfirmRemove(null);
    return run(key, async () => {
      const result = await api.removeUnusedStarterRecords(key);
      setPanel({ kind: 'removed', packKey: key, result });
      await loadPacks();
    }, 'The starter records could not be removed.');
  };

  const toggleContents = (key: types.StarterPackKey) => {
    if (contents[key]) {
      setContents((current) => ({ ...current, [key]: undefined }));
      return;
    }
    setContents((current) => ({ ...current, [key]: 'loading' }));
    api
      .getStarterPack(key)
      .then((pack) => setContents((current) => ({ ...current, [key]: pack })))
      .catch((err) => {
        setContents((current) => ({ ...current, [key]: undefined }));
        setError(getErrorMessage(err, 'The pack contents could not be loaded.'));
      });
  };

  if (!allowed) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <p role="alert" className="text-gray-700">Only an administrator can add industry starter packs.</p>
      </main>
    );
  }

  const created = panel?.kind === 'plan' ? panel.preview : null;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link to="/settings" className="inline-block py-2 sm:py-0 text-sm font-semibold text-gray-500 hover:text-blue-600">
        ← Settings
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-1">Industry starter packs</h1>
      <p className="mt-2 max-w-3xl text-sm text-gray-600">
        A starter pack adds a ready-made set of items, price charts, checklists, lead sources and recurring patterns for one industry. It only
        adds: anything you already have with the same name is left exactly as it is. Everything it adds stays editable, and shows a Starter label
        until you press Keep.
      </p>
      <p role="note" className="mt-3 max-w-3xl rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        {PRICE_REVIEW_NOTE}
      </p>

      {error && <Toast kind="error">{error}</Toast>}
      {loadError && !packs && (
        <div className="mt-4">
          <Toast kind="error">{loadError}</Toast>
          <button type="button" className={`${secondaryButtonClass} mt-2`} onClick={() => void loadPacks()}>
            Try again
          </button>
        </div>
      )}
      {!packs && !loadError && <p role="status" className="mt-6 text-gray-600">Loading starter packs...</p>}

      {packs && (
        <ul aria-label="Starter packs" className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {packs.map((pack) => {
            const busy = busyKey === pack.key;
            const content = contents[pack.key];
            return (
              <li key={pack.key} data-pack={pack.key} className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">{pack.title}</h2>
                  {pack.applied && (
                    <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-800">Applied</span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-600">{pack.description}</p>
                <p className="mt-3 text-sm font-medium text-gray-800">This pack adds:</p>
                <ul className="mt-1 list-disc pl-5 text-sm text-gray-700">
                  {packCountLines(pack.counts).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {pack.applied && (
                  <p className="mt-2 text-sm text-gray-600">
                    {pack.seededCount} {pack.seededCount === 1 ? 'record is' : 'records are'} still labeled Starter.
                  </p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <button type="button" disabled={busyKey !== null} className={`${secondaryButtonClass} ${tap}`} aria-label={`Preview ${pack.title}`} onClick={() => void openPlan(pack.key, 'preview')}>
                    {busy ? 'Working...' : 'Preview'}
                  </button>
                  <button type="button" disabled={busyKey !== null} className={`${primaryButtonClass} ${tap}`} aria-label={`Apply ${pack.title}`} onClick={() => void openPlan(pack.key, 'apply')}>
                    Apply
                  </button>
                  <button type="button" disabled={content === 'loading'} aria-expanded={!!content && content !== 'loading'} className={`${tap} text-sm font-semibold text-blue-600 hover:text-blue-700`} onClick={() => toggleContents(pack.key)}>
                    {content && content !== 'loading' ? 'Hide contents' : content === 'loading' ? 'Loading...' : 'See contents'}
                  </button>
                </div>
                {content && content !== 'loading' && <PackContents pack={content} />}

                {pack.applied && (
                  <div className="mt-3 border-t border-gray-100 pt-3">
                    {confirmRemove === pack.key ? (
                      <div role="group" aria-label={`Confirm removing unused ${pack.title} records`} className="space-y-2">
                        <p className="text-sm text-gray-700">
                          Remove the {pack.title} records that nothing uses and that you have not edited? Items are deleted (their names stay
                          reserved). Anything you changed or have used on a job, invoice, estimate or customer is kept.
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          <button type="button" disabled={busyKey !== null} className={`${rowDangerActionClass} ${tap}`} onClick={() => void remove(pack.key)}>
                            Confirm remove
                          </button>
                          <button type="button" className={`${secondaryButtonClass} ${tap}`} onClick={() => setConfirmRemove(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" disabled={busyKey !== null} className={`${rowDangerActionClass} ${tap}`} aria-label={`Remove unused starter records of ${pack.title}`} onClick={() => setConfirmRemove(pack.key)}>
                        Remove unused starter records
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {packs && panel && (
        <section aria-live="polite" aria-labelledby="starter-panel-heading" className="mt-6 rounded-lg border border-blue-200 bg-white p-4 sm:p-6 shadow">
          {panel.kind === 'plan' && created && (
            <>
              <h2 id="starter-panel-heading" className="text-lg font-bold text-gray-900">
                {panel.intent === 'apply' ? `Apply ${titleOf(packs, panel.packKey)}?` : `Preview: ${titleOf(packs, panel.packKey)}`}
              </h2>
              <p className="mt-1 text-sm text-gray-700">
                Applying adds {created.willCreate} {created.willCreate === 1 ? 'record' : 'records'}
                {(created.willRestore ?? 0) > 0 ? `, restores ${created.willRestore}` : ''} and skips {created.willSkip}. Nothing has been
                changed yet.
              </p>
              {(created.willRestore ?? 0) > 0 && (
                <p className="mt-1 text-sm text-gray-600">Restored items are ones this pack's own Remove deleted earlier; they come back with the pack's definition.</p>
              )}
              <div className="mt-4">
                <EntryList entries={created.entries} />
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button type="button" disabled={busyKey !== null} className={`${primaryButtonClass} ${tap}`} onClick={() => void apply(panel.packKey)}>
                  {busyKey === panel.packKey ? 'Applying...' : 'Apply pack'}
                </button>
                <button type="button" className={`${secondaryButtonClass} ${tap}`} onClick={() => setPanel(null)}>
                  Close
                </button>
              </div>
            </>
          )}
          {panel.kind === 'applied' && (
            <>
              <h2 id="starter-panel-heading" className="text-lg font-bold text-gray-900">{titleOf(packs, panel.packKey)} applied</h2>
              <p role="status" className="mt-1 text-sm text-gray-700">
                Added {panel.result.created.length}
                {(panel.result.restored?.length ?? 0) > 0 ? `, restored ${panel.result.restored?.length}` : ''} and skipped {panel.result.skipped.length}.
              </p>
              <p className="mt-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">{PRICE_REVIEW_NOTE}</p>
              <p className="mt-2 text-sm text-gray-700">
                Review them in{' '}
                <Link to="/settings/items" className="font-semibold text-blue-600 hover:underline">Items</Link>,{' '}
                <Link to="/settings/price-charts" className="font-semibold text-blue-600 hover:underline">Price Charts</Link> and{' '}
                <Link to="/settings/checklist-templates" className="font-semibold text-blue-600 hover:underline">Checklist Templates</Link>.
              </p>
              <div className="mt-4">
                <EntryList
                  doneWording
                  entries={[...panel.result.created, ...(panel.result.restored ?? []), ...panel.result.skipped]}
                />
              </div>
              <button type="button" className={`${secondaryButtonClass} ${tap} mt-4`} onClick={() => setPanel(null)}>
                Close
              </button>
            </>
          )}
          {panel.kind === 'removed' && (
            <>
              <h2 id="starter-panel-heading" className="text-lg font-bold text-gray-900">Unused {titleOf(packs, panel.packKey)} records removed</h2>
              <p role="status" className="mt-1 text-sm text-gray-700">
                Removed {panel.result.removed.length}; kept {panel.result.kept.length}.
              </p>
              {panel.result.removed.length > 0 && (
                <ul aria-label="Removed records" className="mt-3 list-disc pl-5 text-sm text-gray-800">
                  {panel.result.removed.map((r) => (
                    <li key={`${r.entityType}:${r.name}`} className="break-words">{r.name} ({entityLabel(r.entityType)})</li>
                  ))}
                </ul>
              )}
              {panel.result.kept.length > 0 && (
                <>
                  <h3 className="mt-3 text-sm font-semibold text-gray-900">Kept</h3>
                  <ul aria-label="Kept records" className="mt-1 list-disc pl-5 text-sm text-gray-800">
                    {panel.result.kept.map((r) => (
                      <li key={`${r.entityType}:${r.name}`} className="break-words">
                        {r.name} ({entityLabel(r.entityType)}): {KEEP_REASON_TEXT[r.reason] ?? r.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <button type="button" className={`${secondaryButtonClass} ${tap} mt-4`} onClick={() => setPanel(null)}>
                Close
              </button>
            </>
          )}
        </section>
      )}
    </main>
  );
}
